import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexCol,
  FlexBetween,
  FlexRowCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { TableTd, TableRow, DefaultTableTd } from '../General/Table';
import {
  getInvoiceDetailInfo,
  getOrderRowInfo,
  getRequestRowInfo,
} from '../../../utilities/selectors';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { getRequestIcon, getRequestColor } from '../../../utilities';
import { IconImg } from '../../Defaults/Icon/IconImg';
import { Button } from '../General/Button';
import ButtonSecondary from '../../Defaults/Button/ButtonSecondary';

export interface Props {
  request: any;
  theme: any;
  downloadFile: any;
  showCSV?: boolean;
}

function RequestRow({ request, theme, downloadFile, showCSV }: Props) {
  const {
    id,
    date,
    status,
    statusReadable,
    requestNr,
    url,
    yourNr,
  } = getRequestRowInfo(request);
  const color = getRequestColor(status, theme);
  const icon = getRequestIcon(status);

  const handleDownloadFile = () => {
    downloadFile();
  };

  const handleDownloadCsvFile = () => {
    downloadFile('csv');
  };

  return (
    <StyledTableRow>
      <StyledTableTd>
        <LinkToRequest to={url}>
          <CellText>{requestNr}</CellText>
        </LinkToRequest>
      </StyledTableTd>
      <StyledTableTd>
        <CellText>{yourNr}</CellText>
      </StyledTableTd>
      <StyledTableTd>
        <CellText>{date}</CellText>
      </StyledTableTd>
      <StatusTd>
        <SvgIcon
          width={24}
          height={24}
          icon={icon}
          alt="Stav dopytu"
          title="Stav dopytu"
          marginRight={8}
          fill={color}
        />
        <StatusText color={color}>{statusReadable}</StatusText>
      </StatusTd>
      <DownloadTableTd>
        {status === 'INPROGRESS' ? (
          <DownloadButtonsWrapper>
            <FlexRowCenter>
              <StyledButton onClick={handleDownloadFile}>
                <IconImg
                  cursor={'pointer'}
                  width={24}
                  height={24}
                  src={'/assets/dsi/download.svg'}
                  style={{ flexShrink: 0 }}
                  alt={__('Stiahnuť PDF')}
                />
                <DownloadText>PDF</DownloadText>
              </StyledButton>
            </FlexRowCenter>
            {showCSV && (
              <FlexRowCenter>
                <StyledButton onClick={handleDownloadCsvFile}>
                  <IconImg
                    cursor={'pointer'}
                    width={24}
                    height={24}
                    src={'/assets/zemplin/inwork.svg'}
                    style={{ flexShrink: 0 }}
                    alt={__('Stiahnuť CSV')}
                  />
                  <DownloadText>CSV</DownloadText>
                </StyledButton>
              </FlexRowCenter>
            )}
          </DownloadButtonsWrapper>
        ) : (
          <DownloadText style={{ textAlign: 'left' }}>-</DownloadText>
        )}
      </DownloadTableTd>
    </StyledTableRow>
  );
}

const StyledTableTd = styled(TableTd)`
  padding: ${rem(16)} ${rem(24)};
  border-bottom: ${({ theme }) => theme.borders.primary};
`;

const DownloadTableTd = styled(StyledTableTd)`
  padding: ${rem(8)} ${rem(16)};
`;

const DownloadText = styled(TextP)`
  color: ${({ theme }) => theme.colors.primary};
`;

const StyledButton = styled.button`
  border: 0;
  background: transparent;
  display: flex;
  flex-flow: row nowrap;
  gap: ${rem(8)};
  align-items: center;
  cursor: pointer;
`;

const DownloadButtonsWrapper = styled(FlexRowCenter)`
  gap: ${rem(8)};
  justify-content: flex-start;
`;

const StatusTd = styled(StyledTableTd)`
  display: flex;
  align-items: center;
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const LinkToRequest = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    text-decoration: underline;
  }
`;

const CellText = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusText = styled(CellText)<{ color?: string }>`
  color: ${({ theme, color }) => (color ? color : theme.colors.textPrimary)};
`;

const WithThemeRow: any = withTheme(RequestRow);
export { WithThemeRow as RequestRow };
